import { CalculationExpUtil } from './descriptor/calculationExp';
import Equals from './equals';
import CollectionUtils from './collections';
import Utils from './descriptor/descriptorUtils';
import ObjCheck from './objCheck';
import { DescriptorProps } from '../constnats/descriptor';
import { CssProps } from '../constnats/css';

class SchemaDiffUtil {

    /**
     * @param editSchema Required
     * @param reviewSchema Required
     * @param {Object} options Required
     * @param {boolean|undefined} options.ignoreCalcs Optional
     * @param {boolean|undefined} options.labelsAndTypesOnly Optional
     * @returns {diffs:[],descriptorPairs[]} not-null
     */
    static schemaDiff(editSchema, reviewSchema, standardizedLabelsMap, options = { ignoreCalcs: false, labelsAndTypesOnly: false }) {
        const flatEditDescriptors = Utils.flatDescriptors(editSchema.descriptors)
        const flatReviewDescriptors = Utils.flatDescriptors(reviewSchema.descriptors)
        const descriptorPairs = this._mapDescriptors(flatEditDescriptors, flatReviewDescriptors)
        const diffs = this._compareDescriptors(descriptorPairs, flatEditDescriptors, flatReviewDescriptors, standardizedLabelsMap, options)

        if (editSchema.name !== reviewSchema.name) {
            diffs.push(new Diff("Different schema names.", editSchema.name, reviewSchema.name))
        }

        return { diffs, descriptorPairs }
    }

    /**
     * @param {[]} editDescriptors  Required
     * @param {[]} reviewDescriptors Required
     * @returns {[]} not-null
     */
    static _mapDescriptors(editDescriptors, reviewDescriptors) {
        const descriptorPairs = []

        const editDescriptorsCopy = editDescriptors.slice()
        const reviewDescriptorsCopy = reviewDescriptors.slice()

        const reviewMatchHandle = (editDescriptor, reviewDescriptor) => {
            if (reviewDescriptor !== null) {
                const editDescriptorId = editDescriptor.id
                const reviewDescriptorId = reviewDescriptor.id

                SchemaDiffUtil._remove(reviewDescriptorsCopy, reviewDescriptorId)
                SchemaDiffUtil._remove(editDescriptorsCopy, editDescriptorId)
                const editPosition = editDescriptors.findIndex(d => d.id === editDescriptorId)
                const reviewPosition = reviewDescriptors.findIndex(d => d.id === reviewDescriptorId)

                descriptorPairs.push(new DescriptorPair(editDescriptor, reviewDescriptor, editPosition, reviewPosition))
            }
        }

        for (const editDescriptor of editDescriptorsCopy.slice()) {
            reviewMatchHandle(editDescriptor,
                this._mapDescriptorByLabel(editDescriptor, reviewDescriptorsCopy, true))
        }

        for (const editDescriptor of editDescriptorsCopy.slice()) {
            reviewMatchHandle(editDescriptor,
                this._mapDescriptorByLabel(editDescriptor, reviewDescriptorsCopy, false))
        }


        while (CollectionUtils.notEmpty(editDescriptorsCopy) || CollectionUtils.notEmpty(reviewDescriptorsCopy)) {
            if (CollectionUtils.notEmpty(editDescriptorsCopy)) {
                descriptorPairs.push(new DescriptorPair(editDescriptorsCopy.shift(), null))
            }
            if (CollectionUtils.notEmpty(reviewDescriptorsCopy)) {
                descriptorPairs.push(new DescriptorPair(null, reviewDescriptorsCopy.shift()))
            }
        }

        return descriptorPairs
    }

    /**
     * @param {[]} editDescriptor  Required
     * @param {[]} reviewDescriptors Required
     * @param {boolean} strict If the match has to be strict
     * @returns {[]} not-null
     */
    static _mapDescriptorByLabel(editDescriptor, reviewDescriptors, strict) {
        const editDescriptorLabel = SchemaDiffUtil._normalizeStr(editDescriptor.label);

        let reviewDescriptor = reviewDescriptors
            .find(d => SchemaDiffUtil._normalizeStr(d.label) === editDescriptorLabel)


        if (strict === false) {
            reviewDescriptor = reviewDescriptors
                .find(d => SchemaDiffUtil.areClose(d.label, editDescriptorLabel))
        }

        return reviewDescriptor || null
    }

    /**
     * @param {String} label1 Required
     * @param {String} label2 Required
     * @returns boolean
     */
    static areClose(label1, label2) {
        label1 = SchemaDiffUtil._normalizeStr(label1)
        label2 = SchemaDiffUtil._normalizeStr(label2)

        let close;

        if (label1.includes(" ").length > 1 && label1.split(" ").length === label2.split(" ").length) {
            const label1Parts = label1.split(" ")
            const label2Parts = label2.split(" ")
            let partIdx = 0;
            let tmpClose = true;
            while (partIdx < label1Parts.length) {
                tmpClose = tmpClose && SchemaDiffUtil.areClose(label1Parts[partIdx], label2Parts[partIdx])
            }

            close = tmpClose
        } else {
            const l1L = label1.length
            const l2L = label2.length

            const longLabel = l1L >= l2L ? label1 : label2
            const shortLabel = l1L < l2L ? label1 : label2

            close = (longLabel.replace(shortLabel, "").length < (longLabel.length / 5))
        }


        return close
    }

    static _compareDescriptors(descriptorPairs, flatEditDescriptors, flatReviewDescriptors, standardizedLabelsMap, options) {
        const diffs = [];
        for (const descriptorMap of descriptorPairs) {
            const editDescriptor = descriptorMap.editBranchDesc
            const reviewDescriptor = descriptorMap.reviewBranchDesc

            if (editDescriptor === null) {
                diffs.push(new Diff("Missing field.", "", reviewDescriptor.label))
            } else if (reviewDescriptor === null) {
                diffs.push(new Diff("Missing field.", editDescriptor.label, ""))
            } else {
                diffs.push(...this.compareDescriptorPair(descriptorMap,
                    flatEditDescriptors, flatReviewDescriptors, standardizedLabelsMap, options))
            }
        }

        return diffs
    }

    /**
     * @param {DescriptorPair} descriptorMap  Required
     * @param {[]} flatEditDescriptors  Required
     * @param {[]} flatReviewDescriptors  Required
     * @param {Object} options Required
     * @param {boolean} options.ignoreCalcs Required
     * @param {boolean} options.labelsAndTypesOnly Required
     * @returns {[]} not-null
     */
    //TODO Full refactoring
    static compareDescriptorPair(descriptorMap, flatEditDescriptors,
        flatReviewDescriptors, standardizedLabelsMap, options) {

        const normalize = descriptor => {
            //TODO fix generally
            descriptor = JSON.parse(JSON.stringify(descriptor))
            delete descriptor["predefinedValues"]
            delete descriptor["periodToPeriodCalc"]
            if (Object.keys(descriptor.style).length > 0 ) {
                if(descriptor.style["backgroundColor"] === 'inherit') {
                    delete descriptor.style["backgroundColor"]
                }else {
                    descriptor[DescriptorProps.STYLE][CssProps.BACKGROUND_COLOR_PROP] = 
                        Utils.normalizeBackgroundColor(descriptor[DescriptorProps.STYLE][CssProps.BACKGROUND_COLOR_PROP])
                }
                
            }

            return descriptor
        }

        const editDescriptor = normalize(descriptorMap.editBranchDesc)
        const reviewDescriptor = normalize(descriptorMap.reviewBranchDesc)

        const diffs = [];

        if (!SchemaDiffUtil._compareStrings(editDescriptor.label, reviewDescriptor.label)) {
            diffs.push(new Diff("Different labels", editDescriptor.label, reviewDescriptor.label))
            return diffs
        }

        let label = editDescriptor.label
        if (ObjCheck.isNullUndefinedOrEmpty(label)) {
            label = editDescriptor[DescriptorProps.ID]
        }

        if (editDescriptor.type !== reviewDescriptor.type) {
            diffs.push(new Diff(<>Different field type. Field label: <b>{label}</b></>,
                editDescriptor.type, reviewDescriptor.type))
            return diffs
        }

        if (options.labelsAndTypesOnly) {
            return diffs
        }

        diffs.push(...SchemaDiffUtil._subFieldsPropDiff(editDescriptor, reviewDescriptor, label,
            flatEditDescriptors, flatReviewDescriptors))

        const editDescriptorKeys = Object.keys(editDescriptor)
        const reviewDescriptorKeys = Object.keys(reviewDescriptor)
        if (editDescriptorKeys.length !== reviewDescriptorKeys.length) {
            diffs.push(new Diff(<>Different number of props. Field label: <b>{label}</b></>, "", ""))
            return diffs
        }

        for (const editDescriptorKey of editDescriptorKeys) {
            if (editDescriptorKey !== "id" && editDescriptorKey !== "label" && editDescriptorKey !== "subFields" &&
                !Equals.deepEquals(editDescriptor[editDescriptorKey], reviewDescriptor[editDescriptorKey])) {
                if (editDescriptorKey === "calculation" || editDescriptorKey === "secondaryCalculation") {

                    const editDescriptorCalculation = SchemaDiffUtil._normalizeCalculation(editDescriptor[editDescriptorKey], flatEditDescriptors)
                    const reviewDescriptorCalculation = SchemaDiffUtil._normalizeCalculation(reviewDescriptor[editDescriptorKey], flatReviewDescriptors)

                    if (!SchemaDiffUtil._compareStrings(editDescriptorCalculation, reviewDescriptorCalculation) && options.ignoreCalcs !== true) {
                        diffs.push(new Diff(<>Calculation {"(" + editDescriptorKey + ")"}: <br /><b>{label}</b></>,
                            editDescriptorCalculation, reviewDescriptorCalculation))
                    }

                } else if (editDescriptorKey === DescriptorProps.STANDARDIZED_LABEL_RELATION) {
                    const editId = editDescriptor[editDescriptorKey]?.standardizedLabelId;
                    const reviewId = reviewDescriptor[editDescriptorKey]?.standardizedLabelId;
                    if (editId !== reviewId) {
                        diffs.push(new Diff(
                            "Different props values. Field label:" + label + " Field prop: standardizedLabel",
                            JSON.stringify(standardizedLabelsMap[editId]?.name),
                            JSON.stringify(standardizedLabelsMap[reviewId]?.name)
                        ))
                    }
                    const editOverwrite = editDescriptor[editDescriptorKey]?.standardizedLabelOverwrite ?? ""
                    const reviewOverwrite = reviewDescriptor[editDescriptorKey]?.standardizedLabelOverwrite ?? ""
                    if (editOverwrite !== reviewOverwrite) {
                        diffs.push(new Diff(
                            "Different props values. Field label:" + label + " Field prop: standardizedLabelOverwrite",
                            JSON.stringify(editOverwrite),
                            JSON.stringify(reviewOverwrite)
                        ))
                    }
                } else {
                    diffs.push(new Diff("Different props values. Field label:" + label + " Field prop:" + editDescriptorKey,
                        JSON.stringify(editDescriptor[editDescriptorKey]), JSON.stringify(reviewDescriptor[editDescriptorKey])))
                }
            }
        }

        const editPosition = descriptorMap.editPosition
        const reviewPosition = descriptorMap.reviewPosition
        if (diffs.length === 0 && editPosition !== reviewPosition) {
            diffs.push(new Diff(<>Different position. Field label: <b>{label}</b></>,
                "Position:" + editPosition, "Position:" + reviewPosition))
        }

        return diffs
    }

    static _normalizeCalculation(calculationAsPersisted, flatDescriptors) {

        const flatDescriptorsMap = CollectionUtils.arrToMap(flatDescriptors, d => d.id)
        const calcFragments = CalculationExpUtil.strToFragments(calculationAsPersisted)

        for (const calcFragment of calcFragments) {
            if (Array.isArray(calcFragment)) {
                // Possible exception if some of the fields do not exist anymore. It is not supported.
                calcFragment.sort((fieldIdA, fieldIdB) =>
                (SchemaDiffUtil._normalizeStr(flatDescriptorsMap[fieldIdA].label) >
                    SchemaDiffUtil._normalizeStr(flatDescriptorsMap[fieldIdB].label) ? 1 : -1))
            }
        }

        return CalculationExpUtil
            .originalToHumanReadable(CalculationExpUtil.fragmentsToStr(calcFragments), flatDescriptors)
    }

    /**
     * @param {[]} editDescriptor Required
     * @param {[]} reviewDescriptor Required
     * @param {String} label Required
     * @param {[]} flatEditDescriptors Required
     * @param {[]} flatReviewDescriptors Required
     * @returns not-null
     */
    static _subFieldsPropDiff(editDescriptor, reviewDescriptor, label,
        flatEditDescriptors, flatReviewDescriptors) {
        editDescriptor.subFields = editDescriptor.subFields || []
        reviewDescriptor.subFields = reviewDescriptor.subFields || []

        const getLabel = (descriptors, id) => descriptors.find(d => d.id === id).label.trim()
        const getSubFieldLabels = (descriptors, flatDescriptors) =>
            descriptors.subFields.map(d => getLabel(flatDescriptors, d.id))

        const editDescriptorSubFields = getSubFieldLabels(editDescriptor, flatEditDescriptors)
        const reviewDescriptorSubFields = getSubFieldLabels(reviewDescriptor, flatReviewDescriptors)

        const toList = labels => <ol>{labels.map((d, i) => <li key={i}>{d}<br /></li>)}</ol>

        return SchemaDiffUtil._compareStrings(editDescriptorSubFields.join(), reviewDescriptorSubFields.join()) ? [] :
            [new Diff(<>Subfields: <br /><b>{label}</b></>,
                toList(editDescriptorSubFields),
                toList(reviewDescriptorSubFields))]
    }

    /**
     * Removes descriptor by descriptor ID from a descriptors list
     * @param {[]} descriptors Required
     * @param {String} descriptorId Required
     */
    static _remove(descriptors, descriptorId) {
        descriptors.splice(descriptors.findIndex(d => d.id === descriptorId), 1);
    }

    static _compareStrings(str1, str2) {
        return SchemaDiffUtil._normalizeStr(str1) === SchemaDiffUtil._normalizeStr(str2)
    }

    static _normalizeStr(str) {
        return str.trim().toLowerCase()
            .replace(/\s+/g, ' ')
            .replace(/\s*\/\s*/g, "/")
            .replace("–", "-")
            .replace("−", "-")
            .replace("\u2011", "-")
            .replace("\u2010", "-")
    }
}

class Diff {
    static ERROR = "ERROR"
    static WARN = "WARNING"

    constructor(summary, edit = "N/A", review = "N/A", type = Diff.ERROR) {
        this.summary = summary
        this.edit = edit
        this.review = review
        this.type = type
    }
}

class DescriptorPair {
    /**
     * @param  editBranchDesc Optional
     * @param  reviewBranchDesc Optional
     */
    constructor(editBranchDesc, reviewBranchDesc, editPosition, reviewPosition) {
        this.editBranchDesc = editBranchDesc
        this.reviewBranchDesc = reviewBranchDesc
        this.editPosition = editPosition
        this.reviewPosition = reviewPosition
    }
}

export { SchemaDiffUtil, DescriptorPair } 